import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

/**
 * Multi-select dropdown filter component
 * Supports selecting multiple values from a list of options
 */
const MultiSelectFilter = ({
  label,
  icon: Icon,
  value = [], // Array of selected values
  options = [], // Array of { id, name } or strings
  onChange,
  placeholder = 'All',
  maxDisplay = 2, // Max items to show before "+N more"
  className = '',
  disabled = false,
  searchable = true,
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Normalize options to { id, name } format
  const normalizedOptions = options.map(opt => 
    typeof opt === 'string' ? { id: opt, name: opt } : opt
  );

  // Selected values as array
  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  // Filter options based on search
  const filteredOptions = normalizedOptions.filter(opt =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const toggleOption = (optionId) => {
    const newValues = selectedValues.includes(optionId)
      ? selectedValues.filter(v => v !== optionId)
      : [...selectedValues, optionId];
    onChange(newValues);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    
    const selectedNames = selectedValues
      .map(v => normalizedOptions.find(o => o.id === v)?.name)
      .filter(Boolean);
    
    if (selectedNames.length <= maxDisplay) {
      return selectedNames.join(', ');
    }
    return `${selectedNames.slice(0, maxDisplay).join(', ')} +${selectedNames.length - maxDisplay} more`;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-xs font-medium text-[var(--clr-text-muted)] mb-1 flex items-center gap-1">
          {Icon && <Icon className="w-3 h-3" />}
          {label}
        </label>
      )}
      
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        data-testid={testId}
        className={`
          w-full px-3 py-2 text-sm text-left border rounded-lg flex items-center justify-between gap-2
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-[var(--clr-brand-primary)] cursor-pointer'}
          ${isOpen ? 'ring-2 ring-[var(--clr-brand-primary)] border-[var(--clr-brand-primary)]' : 'border-gray-300'}
          ${selectedValues.length > 0 ? 'text-[var(--clr-text-primary)]' : 'text-[var(--clr-text-muted)]'}
        `}
      >
        <span className="truncate flex-1">{getDisplayText()}</span>
        <div className="flex items-center gap-1">
          {selectedValues.length > 0 && (
            <span 
              onClick={clearAll}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[var(--clr-brand-primary)]"
              />
            </div>
          )}
          
          <div className="overflow-y-auto max-h-48">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[var(--clr-text-muted)]">
                No options found
              </div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.id}
                  onClick={() => toggleOption(option.id)}
                  className={`
                    px-3 py-2 text-sm cursor-pointer flex items-center gap-2
                    ${selectedValues.includes(option.id) 
                      ? 'bg-[var(--clr-brand-primary)] bg-opacity-10 text-[var(--clr-brand-primary)]' 
                      : 'hover:bg-gray-50'
                    }
                  `}
                >
                  <div className={`
                    w-4 h-4 border rounded flex items-center justify-center
                    ${selectedValues.includes(option.id) 
                      ? 'bg-[var(--clr-brand-primary)] border-[var(--clr-brand-primary)]' 
                      : 'border-gray-300'
                    }
                  `}>
                    {selectedValues.includes(option.id) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="truncate">{option.name}</span>
                </div>
              ))
            )}
          </div>

          {selectedValues.length > 0 && (
            <div className="p-2 border-t border-gray-100 bg-gray-50">
              <button
                onClick={clearAll}
                className="w-full px-2 py-1 text-xs text-[var(--clr-text-muted)] hover:text-[var(--clr-sev-blocked)]"
              >
                Clear all ({selectedValues.length} selected)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectFilter;
